import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import Button from '../Button';
import Error from '../Error';
import NewAccountScreenLayout from './NewAccountScreenLayout';
import colors from '../../styles/colors';

import step5Image from '../../assets/images/new-account/05.jpg';

const STORAGE_KEY_SMS_OPT_IN = 'newAccount_smsOptIn';

function getSmsOptInFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_SMS_OPT_IN);
    if (raw === 'false') return false;
    if (raw === 'true') return true;
    return true;
  } catch {
    return true;
  }
}

function saveSmsOptInToStorage(value) {
  try {
    sessionStorage.setItem(STORAGE_KEY_SMS_OPT_IN, String(value));
  } catch (err) {
    // ignore
  }
}

const HeadingText = styled.h2`
  color: ${colors.lightBlack};
  font-size: 21px;
  margin: 0 0 0.5em 0;
`;

const Text = styled.p`
  color: ${colors.grey3};
  font-size: 16px;
  line-height: 1.8em;
  margin-bottom: 1.8em;
`;

const PricingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 1.8em;
  flex-wrap: wrap;
`;

const PricingText = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Table = styled.table`
  border: 1px solid gainsboro;
  border-radius: 5px;
  margin: 0;
  padding: 15px;
  flex-shrink: 0;
`;

const TableRow = styled.tr``;

const TableCell = styled.td`
  color: ${colors.grey3};
  font-size: 16px;
  line-height: 1.8em;
  margin-bottom: 1.8em;
  text-align: left;
`;

const TableHeader = styled.th`
  color: ${colors.lightBlack};
  font-size: 16px;
  line-height: 1.8em;
  margin-bottom: 1.8em;
  padding-right: 20px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0;
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin: 0;
  accent-color: ${colors.grey2};
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  color: ${colors.grey3};
  font-size: 16px;
  line-height: 1.8em;
`;

const Link = styled.a`
  color: ${colors.seaGreen};
`;

export default function SmsCampaignOptIn() {
  const history = useHistory();
  const [activateSms, setActivateSms] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    setActivateSms(getSmsOptInFromStorage());
  }, []);

  const handleNext = (e) => {
    e.preventDefault();
    setErrorMsg('');
    saveSmsOptInToStorage(activateSms);
    history.push('/new-account/step/5');
  };

  const handleBack = () => {
    history.push('/new-account/step/3');
  };

  const footer = (
    <>
      <Button type="button" text="Back" onClick={handleBack} width="auto" />
      <Button
        margin="0"
        text="Next"
        type="button"
        onClick={() => document.getElementById('new-account-step4-form')?.requestSubmit()}
        width="auto"
      />
    </>
  );

  return (
    <NewAccountScreenLayout
      stepNumber={4}
      screenImageSrc={step5Image}
      title="Set Up SMS Messaging"
      showSignInLink={false}
      footer={footer}
    >
      <Text>
        SMS and MMS (if you want to include a photo or longer text) text
        campaigns are the easiest, fastest, and most effective way to get
        mouths to your tables. And it has one of the highest returns on
        investment of any marketing channel!
      </Text>
      <HeadingText>Text recipients</HeadingText>
      <Text>
        Unless you select Tags when creating a campaign, all of your contacts
        will receive text campaigns. Opt-out information is automatically
        attached to each campaign.
      </Text>
      <HeadingText>Pricing</HeadingText>
      <PricingRow>
        <PricingText>
          <Text style={{ marginBottom: 0 }}>
            SMS Campaigns: 1 credit per 160 characters
            <br />
            MMS Campaigns: 3 credits
            <br />
            Your credit card will automatically be charged after you send an
            SMS/MMS campaign and charges will appear on your monthly Check This
            Out invoice.
          </Text>
        </PricingText>
        <Table>
          <thead>
            <TableRow>
              <TableHeader>Credits per month</TableHeader>
              <TableHeader>Cost per credit</TableHeader>
            </TableRow>
          </thead>
          <tbody>
            <TableRow>
              <TableCell>Up to 3,000</TableCell>
              <TableCell>$0.036</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>3,001-7,500</TableCell>
              <TableCell>$0.024</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>7,501-25,000</TableCell>
              <TableCell>$0.018</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>25,001-50,000</TableCell>
              <TableCell>$0.015</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>At least 50,001</TableCell>
              <TableCell>$0.014</TableCell>
            </TableRow>
          </tbody>
        </Table>
      </PricingRow>
      <HeadingText>SMS/MMS campaigns</HeadingText>
      <Text>
        Please allow 2-7 days from account activation for your account to be verified by the
        cell carriers before you can send your first SMS/MMS campaign. Check This Out will
        file the verification paperwork for you and let you know when you're all set to send
        campaigns.
      </Text>
      <HeadingText>Terms and conditions</HeadingText>
      <Text>
        By checking the box below you agree to the pricing provided above and
        verify that you have received express written consent to message the
        contacts in your Check This Out account.
        <br />
        All other Check This Out Terms of Use and Privacy Policy is available at{' '}
        <Link href="https://checkthisout.io" target="_blank" rel="noopener noreferrer">
          www.checkthisout.io
        </Link>
        .
      </Text>

      <form id="new-account-step4-form" onSubmit={handleNext}>
        <CheckboxRow>
          <Checkbox
            type="checkbox"
            checked={activateSms}
            onChange={(e) => setActivateSms(e.target.checked)}
          />
          <CheckboxLabel>Activate SMS Campaigns</CheckboxLabel>
        </CheckboxRow>
        {errorMsg && <Error error={errorMsg} margin="16px 0 0 0" />}
      </form>
    </NewAccountScreenLayout>
  );
}
